import { useCallback, useEffect, useMemo } from 'react';

import { graphql, useLazyLoadQuery, usePaginationFragment, useRelayEnvironment } from 'react-relay';
import type { Disposable, IEnvironment } from 'relay-runtime';

import type { useLegacyAgentSessionsData_query$key } from './__generated__/useLegacyAgentSessionsData_query.graphql';
import type { useLegacyAgentSessionsDataPaginationQuery } from './__generated__/useLegacyAgentSessionsDataPaginationQuery.graphql';
import type { useLegacyAgentSessionsDataQuery } from './__generated__/useLegacyAgentSessionsDataQuery.graphql';
import {
	agentSessionsLiveCoordinator,
	type AgentSessionsLiveReader,
} from './agentSessionsLiveCoordinator';
import { getAgentSessionsPolicy } from './agentSessionsPolicy';
import { createAgentSessionsQueryInputs } from './createAgentSessionsQueryInputs';
import { normalizeAgentSession } from './normalizeAgentSession';
import type {
	AgentSession,
	AgentSessionsRefetchOptions,
	AgentSessionsScope,
	AgentSessionsResult,
	RawSessionConnection,
} from './types';
import { useRovoActivationId } from './useRovoActivationId';

/** Internal scope shared by the list read and future lifecycle subscription. */
export type ResolvedAgentSessionsScope = Readonly<{
	accountId: string;
	cloudId?: string;
	rovoActivationId: string;
	workspaceAri: string;
}>;

export type AgentSessionsLiveData = Readonly<{
	environment: IEnvironment;
	reader: AgentSessionsLiveReader;
}>;

export type AgentSessionsData = Readonly<{
	scope: ResolvedAgentSessionsScope | null;
	result: AgentSessionsResult;
	liveData: AgentSessionsLiveData | null;
}>;

export type LegacyAgentSessionsOptions = AgentSessionsScope &
	Readonly<{
		pageSize?: number;
		sort?: string;
		includeLocalAgentSessions?: boolean;
	}>;

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT = 'lastStateChangeAt DESC';

const EMPTY_QUERY_INPUTS = {
	sharingContext: '',
	aqlQuery: '',
} as const;

const mapConnectionNodes = (connection: RawSessionConnection | null): readonly AgentSession[] => {
	return (
		connection?.edges?.flatMap((edge) => {
			const session = edge?.node;
			const normalized = session == null ? null : normalizeAgentSession(session);
			return normalized == null ? [] : [normalized];
		}) ?? []
	);
};

const validatePageSize = (pageSize: number): void => {
	if (!Number.isInteger(pageSize) || pageSize <= 0) {
		throw new RangeError('AgentSessions pageSize must be a positive integer.');
	}
};

export const useLegacyAgentSessionsData = (
	options: LegacyAgentSessionsOptions,
): AgentSessionsData => {
	const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
	const sort = options.sort ?? DEFAULT_SORT;
	validatePageSize(pageSize);

	const activationResolution = useRovoActivationId(options);
	const environment = useRelayEnvironment();
	const shouldReadSessions = activationResolution.status === 'ready';
	const rovoActivationId =
		activationResolution.status === 'ready' ? activationResolution.rovoActivationId : undefined;
	const includeLocalAgentSessions =
		options.includeLocalAgentSessions ?? getAgentSessionsPolicy().includeLocalAgentSessions;
	const queryInputs = useMemo(
		() =>
			rovoActivationId == null
				? EMPTY_QUERY_INPUTS
				: createAgentSessionsQueryInputs(
						options.accountId,
						rovoActivationId,
						includeLocalAgentSessions,
					),
		[options.accountId, includeLocalAgentSessions, rovoActivationId],
	);
	const scope = useMemo<ResolvedAgentSessionsScope | null>(
		() =>
			rovoActivationId == null
				? null
				: {
						accountId: options.accountId,
						...(options.cloudId?.trim() ? { cloudId: options.cloudId.trim() } : {}),
						rovoActivationId,
						workspaceAri: queryInputs.sharingContext,
					},
		[options.accountId, options.cloudId, queryInputs.sharingContext, rovoActivationId],
	);

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks -- This package intentionally exposes the Relay read as its public hook contract.
	const queryData = useLazyLoadQuery<useLegacyAgentSessionsDataQuery>(
		graphql`
			query useLegacyAgentSessionsDataQuery(
				$sharingContext: String!
				$aqlQuery: String!
				$sort: String
				$pageSize: Int
				$after: String
				$shouldReadSessions: Boolean!
			) {
				...useLegacyAgentSessionsData_query
					@arguments(
						sharingContext: $sharingContext
						aqlQuery: $aqlQuery
						sort: $sort
						pageSize: $pageSize
						after: $after
						shouldReadSessions: $shouldReadSessions
					)
			}
		`,
		{
			...queryInputs,
			sort,
			pageSize,
			after: null,
			shouldReadSessions,
		},
		{ fetchPolicy: 'store-or-network' },
	);
	/* eslint-disable @atlassian/relay/graphql-naming -- A pagination driver hook must own its refetchable fragment; there is no rendering component to colocate it with. */
	/* eslint-disable @atlassian/relay/unused-fields -- This is the canonical public raw selection; normalization and consumers read its generated data type outside this GraphQL file. */
	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks -- The public hook deliberately owns the package's private pagination fragment.
	const { data, hasNext, isLoadingNext, loadNext, refetch } = usePaginationFragment<
		useLegacyAgentSessionsDataPaginationQuery,
		useLegacyAgentSessionsData_query$key
	>(
		graphql`
			fragment useLegacyAgentSessionsData_query on Query
			@argumentDefinitions(
				sharingContext: { type: "String!" }
				aqlQuery: { type: "String!" }
				sort: { type: "String" }
				pageSize: { type: "Int", defaultValue: 25 }
				after: { type: "String" }
				shouldReadSessions: { type: "Boolean!" }
			)
			@refetchable(queryName: "useLegacyAgentSessionsDataPaginationQuery") {
				agent_sessionQuery(
					shardingContext: $sharingContext
					aqlQuery: $aqlQuery
					sort: $sort
					first: $pageSize
					after: $after
				)
					@include(if: $shouldReadSessions)
					@connection(
						key: "useAgentSessionsSnapshot_agent_sessionQuery"
						filters: ["shardingContext", "aqlQuery", "sort"]
					) {
					edges {
						node {
							...agentSessionSnapshot_session @relay(mask: false)
						}
					}
					pageInfo {
						hasNextPage
						endCursor
					}
				}
			}
		`,
		queryData,
	);
	/* eslint-enable @atlassian/relay/unused-fields */
	/* eslint-enable @atlassian/relay/graphql-naming */
	const connection = data.agent_sessionQuery ?? null;
	const reader = useMemo<AgentSessionsLiveReader | null>(() => {
		if (scope == null) {
			return null;
		}
		return {
			environment,
			loadedExtent: 0,
			operation: null,
			variables: { ...queryInputs, sort, pageSize, after: null, shouldReadSessions },
			key: JSON.stringify({ ...queryInputs, sort }),
		};
	}, [environment, pageSize, queryInputs, scope, shouldReadSessions, sort]);

	useEffect(() => {
		if (reader == null) {
			return;
		}
		return agentSessionsLiveCoordinator.register(reader);
	}, [reader]);

	useEffect(() => {
		if (reader == null) {
			return;
		}
		reader.loadedExtent = connection?.edges?.length ?? 0;
	}, [connection?.edges?.length, reader]);

	const liveData = useMemo<AgentSessionsLiveData | null>(
		() => (reader == null ? null : { environment, reader }),
		[environment, reader],
	);

	const runRequest = useCallback(
		(
			start: (finish: (error: Error | null) => void) => Disposable,
			onComplete?: (error: Error | null) => void,
		): Disposable => {
			if (reader == null) return { dispose: () => {} };
			reader.operation?.dispose();
			let request: Disposable | undefined;
			let finished = false;
			const finish = (error: Error | null) => {
				if (finished) return;
				finished = true;
				reader.operation = null;
				agentSessionsLiveCoordinator.resume(reader);
				onComplete?.(error);
			};
			const handle = {
				dispose: () => {
					request?.dispose();
					finish(new Error('AgentSessions request was cancelled.'));
				},
			};
			reader.operation = handle;
			agentSessionsLiveCoordinator.pause(reader);
			try {
				request = start(finish);
			} catch (error) {
				finish(error instanceof Error ? error : new Error('AgentSessions request failed.'));
				throw error;
			}
			return handle;
		},
		[reader],
	);

	const refetchSessions = useCallback(
		(refetchOptions?: AgentSessionsRefetchOptions) => {
			if (reader == null) {
				return { dispose: () => {} };
			}
			return runRequest(
				(onComplete) =>
					refetch(
						{ ...reader.variables, pageSize: Math.max(pageSize, connection?.edges?.length ?? 0) },
						{ fetchPolicy: 'network-only', onComplete },
					),
				refetchOptions?.onComplete,
			);
		},
		[connection?.edges?.length, pageSize, reader, refetch, runRequest],
	);

	const loadMore = useCallback(
		(options?: { onComplete?: (error: Error | null) => void }) => {
			if (reader == null || !hasNext || isLoadingNext || reader.operation != null) {
				return;
			}
			runRequest((onComplete) => loadNext(pageSize, { onComplete }), options?.onComplete);
		},
		[hasNext, isLoadingNext, loadNext, pageSize, reader, runRequest],
	);

	const sessions = useMemo(() => mapConnectionNodes(connection), [connection]);

	if (activationResolution.status === 'unavailable') {
		return { scope: null, result: activationResolution, liveData: null };
	}

	return {
		scope,
		result: {
			status: 'ready',
			includesLocalAgentSessions: includeLocalAgentSessions,
			sessions,
			raw: connection,
			pageInfo: {
				hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
				endCursor: connection?.pageInfo?.endCursor ?? null,
			},
			isLoadingMore: isLoadingNext,
			isRefreshing: false,
			loadMoreError: null,
			refreshError: null,
			loadMore,
			refetch: refetchSessions,
		},
		liveData,
	};
};
